import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { z } from 'zod';
import { toast } from 'sonner';
import { useNavigate, useParams } from 'react-router';
import { adminsApi } from '@/lib/api';
import { BaseForm, type FieldConfig } from '@/components/custom/base-form';
import { MAX_PASSWORD_LENGTH, MIN_PASSWORD_LENGTH } from '@/config/constants';
import { PageHeader, PageSection } from '@/components/layouts/page-layout';

type Props = {
  type: 'create' | 'edit';
};

export function AdminAdminsForm({ type }: Props) {
  const { id } = useParams();
  const navigate = useNavigate();

  // Validation with zod
  const formSchema = z.object({
    email: z.email('Please enter a valid email address'),
    display_name: z.string().min(1, 'Display name is required'),
    password:
      type !== 'edit'
        ? z
          .string()
          .min(MIN_PASSWORD_LENGTH, `Password must be at least ${MIN_PASSWORD_LENGTH} characters`)
          .max(MAX_PASSWORD_LENGTH, `Password must be at most ${MAX_PASSWORD_LENGTH} characters`)
        : z
          .string()
          .optional()
          .refine(
            (val) =>
              !val ||
              (val.length >= MIN_PASSWORD_LENGTH && val.length <= MAX_PASSWORD_LENGTH),
            {
              message: `Password must be between ${MIN_PASSWORD_LENGTH} and ${MAX_PASSWORD_LENGTH} characters`,
            }
          ),
  });

  const [defaultValues, setDefaultValues] = useState<Partial<z.infer<typeof formSchema>>>({
    email: "",
    display_name: "",
    password: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setIsLoading(true);
    async function fetchAdmin() {
      if (type === "edit" && id) {
        try {
          const admin = await adminsApi.getById(Number(id));
          setDefaultValues({
            email: admin.email || "",
            display_name: admin.display_name || "",
            password: "",
          });
        } catch (err) {
          setError(
            err instanceof Error ? err.message : "Failed to load admin data."
          );
        }
      } else {
        setDefaultValues({
          email: "",
          display_name: "",
          password: "",
        });
      }
      setIsLoading(false);
    }
    fetchAdmin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, id]);

  // Create field definitions for BaseForm
  const fields: FieldConfig[] = [
    {
      name: "email",
      label: "Email",
      type: "email",
      placeholder: "Enter email address",
    },
    {
      name: "display_name",
      label: "Display Name",
      type: "text",
      placeholder: "Enter display name",
    },
    {
      name: "password",
      label: `Password${type !== "edit" ? " *" : ""}`,
      type: "password",
      placeholder: "Enter password",
    },
  ];

  const handleSubmit = async (values: z.infer<typeof formSchema>) => {
    setError(null);

    setIsSubmitting(true);
    try {
      if (type === "edit" && id) {
        await adminsApi.update(Number(id), {
          email: values.email.trim(),
          display_name: values.display_name.trim(),
          password: values.password ? values.password.trim() : undefined,
        });
        toast.success("Admin updated successfully");
      } else {
        await adminsApi.create({
          email: values.email.trim(),
          display_name: values.display_name.trim(),
          password: values.password!.trim(),
        });
        toast.success("Admin created successfully");
      }
      navigate("/admin/dashboard/admins");
    } catch (err) {
      const message = err instanceof Error ? err.message : "An error occurred";
      setError(message);
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const notes = type !== "edit"
    ? "Password must be at least 8 characters"
    : "Leave \"Password\" blank to keep the current password.";

  return (
    <div className="mx-auto w-full max-w-3xl space-y-6">
      <PageHeader
        title={type === 'edit' ? 'Edit Admin' : 'Create Admin'}
        description={type === 'edit' ? 'Update details for this admin user.' : 'Create a new admin user below.'}
      />

      <PageSection>
        {isLoading && (
          <div className="flex items-center justify-center rounded-xl border border-border bg-card p-8 text-muted-foreground">
            <Loader2 className="mr-2 size-4 animate-spin" />
            Loading admin data...
          </div>
        )}
        {!isLoading && (
          <>
            {error && (
              <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md">
                {error}
              </div>
            )}
            <BaseForm
              schema={formSchema}
              fields={fields}
              defaultValues={defaultValues}
              onSubmit={handleSubmit}
              submitLabel={
                isSubmitting
                  ? "Saving..."
                  : type === "edit"
                    ? "Save Changes"
                    : "Create"
              }
              notes={notes}
              isLoading={isSubmitting}
              onCancel={() => navigate(-1)}
            />
          </>
        )}
      </PageSection>
    </div>
  );
}
